(() => {
  if (window.HederaCommerceLoaded) return;
  window.HederaCommerceLoaded = true;

  const parseSection = (text, selector) => {
    const documentFragment = new DOMParser().parseFromString(text, 'text/html');
    return documentFragment.querySelector(selector);
  };

  class HederaComplementaryProducts extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded === 'true') return;
      this.dataset.loaded = 'true';
      this.primaryHeading ??= this.querySelector('[data-hdc-heading]')?.textContent || '';
      this.primarySubtitle ??= this.querySelector('[data-hdc-subtitle]')?.textContent || '';
      const controller = new AbortController();
      this.controller = controller;
      this.populate(controller.signal).catch(() => {
        if (!controller.signal.aborted) this.hidden = true;
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.dataset.loaded = 'false';
    }

    async request(url, signal) {
      const response = await fetch(url, { signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);
      return response.text();
    }

    async populate(signal) {
      const destination = this.querySelector('[data-hdc-complementary-destination]');
      if (!this.dataset.url || !destination) return;
      this.hidden = true;
      const text = await this.request(this.dataset.url, signal);
      if (signal.aborted) return;
      const sectionId = CSS.escape(this.dataset.sectionId);
      const source = parseSection(text, `template[data-hdc-complementary-list="${sectionId}"]`);
      if (!source) throw new Error('Missing complementary section');
      if (source.content.querySelector('[data-hdc-recommendation-card]')) {
        this.setHeading(this.primaryHeading, this.primarySubtitle);
        destination.replaceChildren(source.content.cloneNode(true));
        this.hidden = false;
        return;
      }

      const selector = `template[data-hdc-fallback-list="${sectionId}"]`;
      let fallback = this.querySelector(selector);
      let grid = null;
      const ids = new Set();
      const visited = new Set();
      const limit = Math.max(1, Math.min(4, Number(this.dataset.limit) || 3));
      while (fallback && !signal.aborted) {
        const sourceGrid = fallback.content.querySelector('.hdc-product-grid');
        if (sourceGrid) {
          if (!grid) grid = sourceGrid.cloneNode(false);
          for (const card of sourceGrid.querySelectorAll('[data-hdc-recommendation-card]')) {
            if (ids.size >= limit) break;
            const id = card.dataset.hdcRecommendationCard;
            if (ids.has(id)) continue;
            ids.add(id);
            grid.append(card.cloneNode(true));
          }
        }
        const nextPage = fallback.dataset.nextPage;
        if (ids.size >= limit || !nextPage || visited.has(nextPage)) break;
        visited.add(nextPage);
        const nextUrl = new URL(this.dataset.productUrl, window.location.origin);
        nextUrl.searchParams.set('section_id', this.dataset.sectionId);
        nextUrl.searchParams.set(fallback.dataset.pageParam || 'page', nextPage);
        try {
          fallback = parseSection(await this.request(nextUrl.toString(), signal), selector);
        } catch (error) {
          if (signal.aborted) return;
          break;
        }
      }
      if (signal.aborted || !grid || !ids.size) return;
      this.setHeading(this.dataset.fallbackHeading, this.dataset.fallbackSubtitle);
      destination.replaceChildren(grid);
      this.hidden = false;
    }

    setHeading(heading, description) {
      this.querySelector('[data-hdc-heading]').textContent = heading;
      const subtitle = this.querySelector('[data-hdc-subtitle]');
      subtitle.textContent = description || '';
      subtitle.hidden = !subtitle.textContent.trim();
    }
  }

  if (!customElements.get('hedera-complementary-products')) {
    customElements.define('hedera-complementary-products', HederaComplementaryProducts);
  }
})();
